// Sugalarm — kiosk-frame footer nav. Cycle 64 turned the original
// 64-px-tall, full-width, opaque bottom bar into a floating
// translucent surface overlaid on the screen content. Cycle 72
// reshaped that surface from a fully-rounded pill (`border-radius:
// 999px`, themed blue/navy fill) into a **rounded rectangle whose
// corner radius matches the Now-screen graph panel** (via the shared
// `--kiosk-panel-radius` token on `.kiosk`), with a **translucent
// black** fill (`--kiosk-nav-fill`, theme-neutral) so the active
// item's accent tint pops.
//
// Cycle 111 re-anchored the bottom nav to the **flush-to-bottom-edge
// pill** language the cycle-103 side rail uses: the background now runs
// all the way to y=600 (the bottom screen edge) — `bottom: 0`, no float
// gap below it — with only the **top-left and top-right corners**
// rounded (`--kiosk-panel-radius`) and **square bottom corners** (off
// the visible edge). The hairline border paints on the visible edges
// only (top/left/right; none along the flush bottom), and the floating
// shadow casts **upward** (negative y offset) so nothing renders below
// the display edge. Width + horizontal centering are unchanged — it
// still sits `--kiosk-nav-pill-inset` (15px) in from each side, reading
// as a snug tab emerging from the bottom edge, mirroring how the side
// pill emerges from the left edge. Fill (`--kiosk-nav-fill` + backdrop
// blur), 52-px height, 5-column grid, z-index 40, and every tab item
// (order, routing, glyphs, labels, active accent) are unchanged.
//
// Geometry tokens live in `theme/tokens.css` under `.kiosk`. With the
// float gap gone, `--kiosk-nav-pill-inset` is now only the left/right
// inset, and `--kiosk-nav-safe-area` (the `padding-bottom` the
// `.kioskContent` wrapper applies so the measured-fill chain — cycle
// 34/48 chart, cycle 58.2 numpad — bottoms out above the nav) is
// re-derived from the nav's new top edge: `pill-height + 8px` gap.
//
// Kiosk-only — only the `variant === "kiosk"` branch of `KioskFrame`
// mounts this component. The web `EdgeRail` and the `MobileFrame`
// bottom tab bar are unrelated surfaces and were not touched here.

import { useAttributionPickerOpen } from "../../lib/attributionPicker";
import { NavLink, useLocation } from "react-router-dom";

import { enterClockMode } from "../../lib/clockMode";
import { NAV_ITEMS, NavIcon } from "./NavIcon";

interface Props {
  // Cycle 42: retained on the prop surface but no longer rendered —
  // the Alerts nav entry this badged was retired.
  unackCount: number;
}

export function FooterTabs(_props: Props) {
  const location = useLocation();
  const pickerDim = useAttributionPickerOpen();
  const renderTab = (n: (typeof NAV_ITEMS)[number]) => (
    <NavLink
      key={n.id}
      to={n.path}
      style={({ isActive }) => ({
        appearance: "none",
        border: 0,
        background: isActive ? "var(--accent-soft)" : "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 2,
        position: "relative",
        color: isActive ? "var(--accent)" : "var(--ink)",
        textDecoration: "none",
        fontWeight: 600,
        fontSize: "var(--type-floor)",
        // Inner sub-rect behind the active item so it stays
        // distinguishable against a varying garden — see the cycle-72
        // radius derivation note in the git history of this file.
        borderRadius: "calc(var(--kiosk-panel-radius, 14px) - 4px)",
        margin: "4px 2px",
        transition: "background 160ms, color 160ms",
      })}
    >
      {({ isActive }) => (
        <>
          <NavIcon id={n.id} size={20} active={isActive} />
          <span style={{ letterSpacing: "0.01em" }}>{n.label}</span>
        </>
      )}
    </NavLink>
  );
  return (
    <div
      style={{
        position: "absolute",
        // Cycle 111: flush to the bottom screen edge (bottom: 0) — the
        // cycle-64/72 float gap below the pill is gone. Left/right keep
        // the horizontal inset so width + centering are unchanged.
        left: "var(--kiosk-nav-pill-inset)",
        right: "var(--kiosk-nav-pill-inset)",
        bottom: 0,
        height: "var(--kiosk-nav-pill-height)",
        // Cycle 456: dimmed and inert while "Who's treating?" is up.
        ...(pickerDim
          ? { opacity: 0.35, pointerEvents: "none" as const, transition: "opacity 160ms" }
          : {}),
        display: "grid",
        // Cycle 145: 5 routed tabs + the appended Clock ACTION item.
        gridTemplateColumns: "repeat(6, 1fr)",
        alignItems: "stretch",
        background: "var(--kiosk-nav-fill)",
        // Chromium-on-Pi needs the -webkit- prefix; the unprefixed
        // property is the modern fallback.
        WebkitBackdropFilter: "blur(14px) saturate(140%)",
        backdropFilter: "blur(14px) saturate(140%)",
        // Cycle 111: hairline on the VISIBLE edges only (top/left/right);
        // the flush bottom edge is off-screen, so no bottom border —
        // mirroring the cycle-103 side-rail pill.
        borderTop: "0.5px solid var(--line-strong)",
        borderLeft: "0.5px solid var(--line-strong)",
        borderRight: "0.5px solid var(--line-strong)",
        // Cycle 72/111: radius matches the Now-screen graph panel via the
        // shared --kiosk-panel-radius token. Cycle 111: only the two TOP
        // corners are rounded (the bottom corners sit off the visible
        // edge), so the nav reads as a tab emerging from the bottom edge.
        borderTopLeftRadius: "var(--kiosk-panel-radius, 14px)",
        borderTopRightRadius: "var(--kiosk-panel-radius, 14px)",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        // Cycle 111: cast the floating shadow UPWARD (negative y) so it
        // lifts off the content above and nothing paints below the
        // flush bottom edge (which `.kiosk { overflow: hidden }` would
        // clip anyway).
        boxShadow: "0 -6px 24px rgba(0, 0, 0, 0.18)",
        // Sits above normal kiosk chrome but below cycle-63
        // `AlarmSilenceGuard` (z-index 60) and cycle-42 `UrgentOverlay`
        // (z-index 100) so an urgent takeover still covers the nav.
        zIndex: 40,
        padding: "0 6px",
        // Defensive — the pill is the only chrome floating over the
        // kiosk content area; let pointer events pass through any
        // hairline transparent edge to the nav buttons within.
        pointerEvents: "auto",
      }}
    >
      {/* Cycle 146: the Clock ACTION item slots BEFORE Settings so
          Settings stays the last item on every nav surface. Still not
          a routed tab — no active state, NAV_ITEMS untouched. */}
      {NAV_ITEMS.slice(0, -1).map(renderTab)}
      <button
        type="button"
        aria-label="Alarm clock mode"
        onClick={() =>
          enterClockMode("nav", { priorPath: location.pathname })
        }
        style={{
          appearance: "none",
          border: 0,
          background: "transparent",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          color: "var(--ink)",
          fontFamily: "inherit",
          fontWeight: 600,
          fontSize: "var(--type-floor)",
          borderRadius: "calc(var(--kiosk-panel-radius, 14px) - 4px)",
          margin: "4px 2px",
          cursor: "pointer",
        }}
      >
        <NavIcon id="clock" size={20} />
        <span style={{ letterSpacing: "0.01em" }}>Clock</span>
      </button>
      {NAV_ITEMS.slice(-1).map(renderTab)}
    </div>
  );
}
